import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { App as AntdApp, ConfigProvider, theme, Spin } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import AuthLayout from '@/layouts/AuthLayout';
import LoginPage from '@/pages/login/LoginPage';

const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const OperationsPage = lazy(() => import('@/pages/operations/OperationsPage'));
const DirectoryPage = lazy(() => import('@/pages/directory/DirectoryPage'));
const UserListPage = lazy(() => import('@/pages/users/UserListPage'));
const UserDetailPage = lazy(() => import('@/pages/users/UserDetailPage'));
const AnalyticsPage = lazy(() => import('@/pages/analytics/AnalyticsPage'));
const CareObservatoryPage = lazy(() => import('@/pages/care-observatory/CareObservatoryPage'));
const LLMCallLogPage = lazy(() => import('@/pages/llm-calls/LLMCallLogPage'));
const LLMModelsPage = lazy(() => import('@/pages/llm-models/LLMModelsPage'));
const MiniProgramFeatureSettingsPage = lazy(
  () => import('@/pages/mini-program-feature-settings/MiniProgramFeatureSettingsPage'),
);
const StorageTestPage = lazy(() => import('@/pages/storage-test/StorageTestPage'));
const PetListPage = lazy(() => import('@/pages/pets/PetListPage'));
const PetDetailPage = lazy(() => import('@/pages/pets/PetDetailPage'));
const RecordExportTestPage = lazy(() => import('@/pages/record-exports/RecordExportTestPage'));
const CheckupReportListPage = lazy(() => import('@/pages/checkups/CheckupReportListPage'));
const FoodListPage = lazy(() => import('@/pages/foods/FoodListPage'));
const FoodMeasuredValuesPage = lazy(() => import('@/pages/foods/FoodMeasuredValuesPage'));
const TreatmentListPage = lazy(() => import('@/pages/treatments/TreatmentListPage'));
const TreatmentSubmissionListPage = lazy(
  () => import('@/pages/treatment-submissions/TreatmentSubmissionListPage'),
);
const TreatmentSubmissionDetailPage = lazy(
  () => import('@/pages/treatment-submissions/TreatmentSubmissionDetailPage'),
);
const FoodSubmissionDetailPage = lazy(
  () => import('@/pages/food-submissions/FoodSubmissionDetailPage'),
);
const FoodIssueReportDetailPage = lazy(
  () => import('@/pages/food-reports/FoodIssueReportDetailPage'),
);
const LabExtractSettingsPage = lazy(() => import('@/pages/lab-settings/LabExtractSettingsPage'));
const FoodExtractionSettingsPage = lazy(
  () => import('@/pages/food-extraction/FoodExtractionSettingsPage'),
);
const CheckupDiagnosisSettingsPage = lazy(
  () => import('@/pages/checkup-diagnosis/CheckupDiagnosisSettingsPage'),
);
const CareAnalysisSettingsPage = lazy(
  () => import('@/pages/care-analysis/CareAnalysisSettingsPage'),
);
const VisitReportValidationPage = lazy(
  () => import('@/pages/visit-reports/VisitReportValidationPage'),
);
const NutritionProfileListPage = lazy(
  () => import('@/pages/nutrition-profiles/NutritionProfileListPage'),
);
const FeedbackListPage = lazy(() => import('@/pages/feedback/FeedbackListPage'));
const FeedbackDetailPage = lazy(() => import('@/pages/feedback/FeedbackDetailPage'));

const PageFallback: React.FC = () => (
  <div className="purrcare-page-fallback">
    <Spin size="large" />
  </div>
);

const App: React.FC = () => {
  return (
    <ConfigProvider
      locale={zhCN}
      theme={{
        algorithm: theme.defaultAlgorithm,
        token: {
          colorPrimary: '#285b54',
          colorInfo: '#527f76',
          colorSuccess: '#5f856e',
          colorWarning: '#bd814b',
          colorError: '#b55d46',
          colorText: '#203b38',
          colorTextSecondary: '#71807c',
          colorBgLayout: '#f4efe5',
          colorBgContainer: '#fffdf8',
          colorBgElevated: '#fffdf8',
          colorBorder: '#dfe4da',
          colorBorderSecondary: '#e9ebe3',
          borderRadius: 10,
          borderRadiusLG: 16,
          controlHeight: 36,
          fontFamily:
            'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", sans-serif',
          boxShadowTertiary: '0 10px 28px rgba(31, 70, 65, 0.07)',
        },
        components: {
          Layout: {
            bodyBg: '#f4efe5',
            headerBg: '#fffdf8',
            siderBg: '#fffdf8',
          },
          Menu: {
            itemBg: 'transparent',
            itemColor: '#516a66',
            itemHoverBg: '#f3f1e8',
            itemHoverColor: '#1f514b',
            itemSelectedBg: '#e8eee5',
            itemSelectedColor: '#1f514b',
            itemBorderRadius: 10,
            groupTitleColor: '#9b8170',
          },
          Card: {
            headerBg: '#fffdf8',
          },
          Table: {
            headerBg: '#f0f2eb',
            headerColor: '#45615d',
            rowHoverBg: '#faf8f1',
          },
          Button: {
            primaryShadow: 'none',
          },
          Tabs: {
            itemSelectedColor: '#285b54',
            inkBarColor: '#b8734d',
          },
        },
      }}
    >
      <AntdApp className="purrcare-app">
        <BrowserRouter>
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/" element={<AuthLayout />}>
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="operations" element={<OperationsPage />} />
                <Route path="directory" element={<DirectoryPage />} />
                <Route path="users" element={<UserListPage />} />
                <Route path="users/:id" element={<UserDetailPage />} />
                <Route path="analytics" element={<AnalyticsPage />} />
                <Route path="care-observatory" element={<CareObservatoryPage />} />
                <Route path="llm-calls" element={<LLMCallLogPage />} />
                <Route path="llm-models" element={<LLMModelsPage />} />
                <Route
                  path="mini-program-feature-settings"
                  element={<MiniProgramFeatureSettingsPage />}
                />
                <Route path="storage-test" element={<StorageTestPage />} />
                <Route path="pets" element={<PetListPage />} />
                <Route path="pets/:id" element={<PetDetailPage />} />
                <Route path="record-exports" element={<RecordExportTestPage />} />
                <Route path="checkup-reports" element={<CheckupReportListPage />} />
                <Route path="foods" element={<FoodListPage />} />
                <Route path="foods/:foodId/measured-values" element={<FoodMeasuredValuesPage />} />
                <Route path="food-reports" element={<Navigate to="/foods?tab=reports" replace />} />
                <Route path="food-reports/:id" element={<FoodIssueReportDetailPage />} />
                <Route path="treatments" element={<TreatmentListPage />} />
                <Route path="treatment-submissions" element={<TreatmentSubmissionListPage />} />
                <Route
                  path="treatment-submissions/:id"
                  element={<TreatmentSubmissionDetailPage />}
                />
                <Route
                  path="food-submissions"
                  element={<Navigate to="/foods?tab=submissions" replace />}
                />
                <Route path="food-submissions/:id" element={<FoodSubmissionDetailPage />} />
                <Route path="lab-settings" element={<LabExtractSettingsPage />} />
                <Route path="food-extraction-settings" element={<FoodExtractionSettingsPage />} />
                <Route
                  path="checkup-diagnosis-settings"
                  element={<CheckupDiagnosisSettingsPage />}
                />
                <Route path="care-analysis-settings" element={<CareAnalysisSettingsPage />} />
                <Route path="visit-reports" element={<VisitReportValidationPage />} />
                <Route path="nutrition-profiles" element={<NutritionProfileListPage />} />
                <Route path="feedback" element={<FeedbackListPage />} />
                <Route path="feedback/:id" element={<FeedbackDetailPage />} />
              </Route>
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AntdApp>
    </ConfigProvider>
  );
};

export default App;
